import { api } from 'src/api/api';

import type { FicFidcCockpitConcentrationV1Payload } from 'src/pages/Monitor/components/FicFidcCockpit/concentration/contract';

export interface MetaCarteirasFicFidcsCarouselItem {
  name: string;
  operationId: string;
  pl?: number | null;
  cnpj?: string | null;
  badge?: string | null;
  slug?: string | null;
  market?: string | null;
}

export interface MetaCarteirasFicFidcsCarouselParams {
  limit?: number;
  orderBy?: string;
  market?: string;
}

export interface MetaCarteirasFicFidcsCarouselResponse {
  data: MetaCarteirasFicFidcsCarouselItem[];
  meta: {
    limit: number;
    orderBy: string;
    market: string;
  };
}

/**
 * Data de competência (yyyy-MM-dd) opcional. Sem `date`, o backend retorna o
 * snapshot mais recente (comportamento legado preservado). Quando informada, a
 * data é autoritativa: a resposta reflete exclusivamente a competência pedida.
 */
export interface MetaCarteirasFicFidcsChildrenParams {
  date?: string;
}

export interface MetaCarteirasFicFidcsWalletHistoryParams {
  date?: string;
}

export interface MetaCarteirasFicFidcsIsFicParams {
  date?: string;
}

export interface MetaCarteirasFicFidcsChildPosition {
  name: string;
  cnpj: string | null;
  vlMercPosFinal: number | null;
  dtComptc: string;
  operationId: string | null;
  slug: string | null;
  ativoLastro: string | null;
  percentage: number | null;
}

export interface MetaCarteirasFicFidcsChildTrade {
  name: string;
  cnpj: string | null;
  qtVendaNegoc: number | null;
  vlVendaNegoc: number | null;
  qtAquisNegoc: number | null;
  vlAquisNegoc: number | null;
  dtComptc: string;
  operationId: string | null;
  slug: string | null;
}

export interface MetaCarteirasFicFidcsChildrenMeta {
  total: number;
  baseMonth?: string;
  totalVlMercPosFinal?: number | null;
  totalPercentage?: number | null;
}

export interface MetaCarteirasFicFidcsChildrenResponse {
  positions: {
    data: MetaCarteirasFicFidcsChildPosition[];
    meta: MetaCarteirasFicFidcsChildrenMeta;
  };
  trades: {
    data: MetaCarteirasFicFidcsChildTrade[];
    meta: MetaCarteirasFicFidcsChildrenMeta;
  };
}

export interface MetaCarteirasFicFidcsWalletHistoryItem {
  competence: string;
  totalVlMercPosFinal: number;
}

export interface MetaCarteirasFicFidcsWalletHistoryResponse {
  data: MetaCarteirasFicFidcsWalletHistoryItem[];
}

export interface IsFicResponse {
  isFic: boolean;
}

export type MetaCarteirasFicFidcsConcentrationResponse =
  FicFidcCockpitConcentrationV1Payload;

export type FicChildTradesHistoryPeriod = '6m' | '12m' | 'all';

export interface FicChildTradesHistoryTotals {
  purchases: number;
  sales: number;
  net: number;
}

export interface FicChildTradesHistoryItem {
  competence: string;
  purchases: number;
  sales: number;
  net: number;
}

export interface FicChildTradesHistoryMeta {
  period: FicChildTradesHistoryPeriod;
  startCompetence: string | null;
  endCompetence: string | null;
  totals: FicChildTradesHistoryTotals;
}

export interface FicChildTradesHistoryResponse {
  data: FicChildTradesHistoryItem[];
  meta: FicChildTradesHistoryMeta;
}

const DEFAULT_CAROUSEL_PARAMS = {
  limit: 15,
  orderBy: 'pl',
  market: 'FIDC',
} as const;

export async function getMetaCarteirasFicFidcsCarousel(
  params: MetaCarteirasFicFidcsCarouselParams = {},
) {
  const { data } = await api.get<MetaCarteirasFicFidcsCarouselResponse>(
    '/meta-carteiras-fic-fidcs/carousel',
    {
      params: { ...DEFAULT_CAROUSEL_PARAMS, ...params },
    },
  );

  return data;
}

export async function registerMetaCarteirasFicFidcsCarouselHit(
  operationId: string,
) {
  const { data } = await api.post(
    `/meta-carteiras-fic-fidcs/carousel/${operationId}/hit`,
  );

  return data;
}

export async function getMetaCarteirasFicFidcsChildren(
  operationId: string,
  params: MetaCarteirasFicFidcsChildrenParams = {},
) {
  const { date } = params;

  if (date) {
    const { data } = await api.get<MetaCarteirasFicFidcsChildrenResponse>(
      `/meta-carteiras-fic-fidcs/${operationId}/children`,
      { params: { date } },
    );

    return data;
  }

  const { data } = await api.get<MetaCarteirasFicFidcsChildrenResponse>(
    `/meta-carteiras-fic-fidcs/${operationId}/children`,
  );

  return data;
}

export async function getMetaCarteirasFicFidcsTradesHistory(
  operationId: string,
  period: FicChildTradesHistoryPeriod = '12m',
  date?: string,
) {
  const params = date ? { period, date } : { period };
  const { data } = await api.get<FicChildTradesHistoryResponse>(
    `/meta-carteiras-fic-fidcs/${operationId}/children/trades-history`,
    {
      params,
    },
  );

  return data;
}

export async function getMetaCarteirasFicFidcsWalletHistory(
  operationId: string,
  params: MetaCarteirasFicFidcsWalletHistoryParams = {},
) {
  const { date } = params;

  if (date) {
    const { data } = await api.get<MetaCarteirasFicFidcsWalletHistoryResponse>(
      `/meta-carteiras-fic-fidcs/${operationId}/wallet-history`,
      { params: { date } },
    );

    return data;
  }

  const { data } = await api.get<MetaCarteirasFicFidcsWalletHistoryResponse>(
    `/meta-carteiras-fic-fidcs/${operationId}/wallet-history`,
  );

  return data;
}

export async function getIsFic(
  operationId: string,
  params: MetaCarteirasFicFidcsIsFicParams = {},
) {
  const { date } = params;

  if (date) {
    const { data } = await api.get<IsFicResponse>(
      `/meta-carteiras-fic-fidcs/${operationId}/is-fic`,
      { params: { date } },
    );

    return data;
  }

  const { data } = await api.get<IsFicResponse>(
    `/meta-carteiras-fic-fidcs/${operationId}/is-fic`,
  );

  return data;
}

export async function getMetaCarteirasFicFidcsConcentration(
  operationId: string,
  date?: string,
) {
  const { data } = await api.get<MetaCarteirasFicFidcsConcentrationResponse>(
    `/meta-carteiras-fic-fidcs/${operationId}/concentration`,
    {
      params: date ? { date } : undefined,
    },
  );

  return data;
}
